import React, { useState, useEffect } from 'react';
import { X, Send, Mail, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { sendInvoiceEmail } from '../../services/invoiceEmailService';
import { getInvoicePDFUrl } from '../../services/invoiceStorageService';
import { db } from '../../firebase';
import { doc, updateDoc, collection, query, where, getDocs, Timestamp, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Modal pour envoyer une facture par email
 */
const SendInvoiceModal = ({ isOpen, onClose, invoice, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emailData, setEmailData] = useState({
    to: '',
    name: '',
    includePdf: true,  // Activé par défaut - génération PDF avec html2pdf.js
    includeLink: true,
    message: ''
  });

  // Mettre à jour les données email quand la facture change
  useEffect(() => {
    if (invoice?.client) {
      setEmailData(prev => ({
        ...prev,
        to: invoice.client.email || '',
        name: invoice.client.nom || ''
      }));
    }
  }, [invoice]);

  if (!isOpen || !invoice) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      console.log('📧 Envoi facture par email...', invoice.id);

      // Vérifier si un PDF existe déjà dans Storage
      let pdfAttachment = null;
      let pdfUrl = null;

      if (emailData.includePdf) {
        // Vérifier si le PDF existe déjà dans Firestore
        const billingInvoiceRef = doc(db, 'stripeBillingInvoices', invoice.id);
        const invoiceDoc = await getDoc(billingInvoiceRef);
        const existingPdfUrl = invoiceDoc.exists() ? invoiceDoc.data().pdfUrl : null;

        if (existingPdfUrl) {
          console.log('📄 PDF déjà existant, récupération depuis Storage...');
          try {
            pdfUrl = await getInvoicePDFUrl(invoice.id);
            if (pdfUrl) {
              console.log('✅ PDF existant trouvé, récupération du Blob...');
              // Utiliser mode 'no-cors' pour éviter les problèmes CORS en développement
              const response = await fetch(pdfUrl, {
                mode: 'cors',
                credentials: 'omit',
                headers: {
                  'Accept': 'application/pdf'
                }
              });

              if (response.ok) {
                pdfAttachment = await response.blob();
              } else {
                throw new Error('Échec récupération PDF');
              }
            }
          } catch (error) {
            console.warn('⚠️ PDF existant invalide ou problème CORS, régénération...', error.message);
            pdfUrl = null;
          }
        }

        // Générer le PDF avec Cloud Function si nécessaire
        if (!pdfUrl) {
          console.log('📄 Génération du PDF avec Cloud Function...');
          // Spécifier la région europe-west1 où la fonction est déployée
          const functions = getFunctions(undefined, 'europe-west1');
          const generatePdf = httpsCallable(functions, 'generateInvoicePdf');

          const result = await generatePdf({ invoiceId: invoice.id });

          if (result.data.success) {
            pdfUrl = result.data.pdfUrl;
            console.log('✅ PDF généré par Cloud Function:', pdfUrl);

            // Récupérer le PDF en Blob pour l'email
            const response = await fetch(pdfUrl);
            pdfAttachment = await response.blob();
          } else {
            throw new Error('Échec de la génération du PDF');
          }
        }
      }

      // Envoyer l'email
      const result = await sendInvoiceEmail(
        invoice,
        emailData.to,
        emailData.name,
        pdfAttachment
      );

      console.log('✅ Email envoyé avec succès:', result);

      // Mettre à jour le statut de la facture à "envoyée"
      try {
        console.log('📝 Mise à jour du statut de la facture...');

        // Préparer les données de mise à jour
        const updateData = {
          statut: 'envoyée',
          emailEnvoye: true,
          dateEnvoiEmail: Timestamp.now(),
          emailDestination: emailData.to
        };

        // Ajouter l'URL du PDF si généré
        if (pdfUrl) {
          updateData.pdfUrl = pdfUrl;
          updateData.pdfPath = `invoices/${invoice.id}.pdf`;
          updateData.pdfGeneratedAt = Timestamp.now();
        }

        // Mettre à jour dans stripeBillingInvoices
        const billingInvoiceRef = doc(db, 'stripeBillingInvoices', invoice.id);
        await updateDoc(billingInvoiceRef, updateData);

        // Mettre à jour dans clientInvoices (rechercher par invoiceNumber)
        const clientInvoicesRef = collection(db, 'clientInvoices');
        const q = query(clientInvoicesRef, where('invoiceNumber', '==', invoice.id));
        const snapshot = await getDocs(q);

        for (const docSnapshot of snapshot.docs) {
          await updateDoc(doc(db, 'clientInvoices', docSnapshot.id), {
            status: 'sent',
            sentAt: Timestamp.now(),
            sentTo: emailData.to
          });
        }

        console.log('✅ Statut de la facture mis à jour');
      } catch (updateError) {
        console.error('⚠️ Erreur lors de la mise à jour du statut:', updateError);
        // Ne pas bloquer le succès de l'envoi si la mise à jour échoue
      }

      // Notifier le succès
      if (onSuccess) {
        onSuccess({
          message: 'Email envoyé avec succès',
          invoice: invoice,
          result: result
        });
      }

      // Fermer le modal
      onClose();

    } catch (err) {
      console.error('❌ Erreur envoi email:', err);
      setError(err.message || 'Échec de l\'envoi de l\'email');
    } finally {
      setLoading(false);
    }
  };

  const formatEuro = (montant) => {
    if (typeof montant === 'undefined' || montant === null) return '0,00 €';
    return montant.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('fr-FR');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* En-tête */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex justify-between items-center">
          <div className="flex items-center">
            <Mail className="w-6 h-6 text-blue-500 mr-3" />
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Envoyer la facture par email
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Facture N° {invoice.id}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Contenu */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Informations de la facture */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
            <div className="flex items-start space-x-3">
              <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                  Détails de la facture
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-blue-600 dark:text-blue-400">Période :</span>
                    <span className="ml-2 text-blue-900 dark:text-blue-100">
                      {invoice.periode?.libelle || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-blue-600 dark:text-blue-400">Montant :</span>
                    <span className="ml-2 font-bold text-blue-900 dark:text-blue-100">
                      {formatEuro(invoice.calcul?.montantTotal || 0)}
                    </span>
                  </div>
                  <div>
                    <span className="text-blue-600 dark:text-blue-400">Date :</span>
                    <span className="ml-2 text-blue-900 dark:text-blue-100">
                      {formatDate(invoice.dateGeneration)}
                    </span>
                  </div>
                  <div>
                    <span className="text-blue-600 dark:text-blue-400">Statut :</span>
                    <span className="ml-2 text-blue-900 dark:text-blue-100">
                      {invoice.statut}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Formulaire */}
          <div className="space-y-4">
            {/* Email destinataire */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email du destinataire *
              </label>
              <input
                type="email"
                value={emailData.to}
                onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="client@example.com"
                required
                disabled={loading}
              />
            </div>

            {/* Nom destinataire */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nom du destinataire *
              </label>
              <input
                type="text"
                value={emailData.name}
                onChange={(e) => setEmailData({ ...emailData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Nom du client"
                required
                disabled={loading}
              />
            </div>

            {/* Options d'envoi */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Options d'envoi
              </label>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="includePdf"
                  checked={emailData.includePdf}
                  onChange={(e) => setEmailData({ ...emailData, includePdf: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                />
                <div className="ml-2">
                  <label htmlFor="includePdf" className="text-sm text-gray-700 dark:text-gray-300">
                    Inclure le PDF en pièce jointe
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    La facture sera jointe au format PDF
                  </p>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="includeLink"
                  checked={emailData.includeLink}
                  onChange={(e) => setEmailData({ ...emailData, includeLink: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  disabled={loading}
                />
                <label htmlFor="includeLink" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Inclure le lien vers l'espace client
                </label>
              </div>
            </div>

            {/* Message personnalisé */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Message personnalisé (optionnel)
              </label>
              <textarea
                value={emailData.message}
                onChange={(e) => setEmailData({ ...emailData, message: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                rows="3"
                placeholder="Ajoutez un message personnalisé (optionnel)"
                disabled={loading}
              />
            </div>
          </div>

          {/* Aperçu du sujet */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Sujet de l'email :
            </p>
            <p className="text-sm text-gray-900 dark:text-white font-mono">
              Votre facture OMAD Services - {invoice.periode?.libelle || 'Facture'}
            </p>
          </div>

          {/* Message d'erreur */}
          {error && (
            <div className="flex items-start space-x-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  Erreur lors de l'envoi
                </p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  {error}
                </p>
              </div>
            </div>
          )}

          {/* Boutons d'action */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex items-center px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer la facture
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SendInvoiceModal;
